import React, { Fragment, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useTranslate, useNotify } from 'react-admin';
import { billingFormServices } from 'src/services/payment/billingForm/billingForm.service';
import { Add } from '@mui/icons-material';
import { Dialog, FormHelperText, DialogTitle, DialogContent, DialogActions, Grid, Skeleton, Typography, TextField, Button, Icon, Autocomplete, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import CurrencyInput from 'src/components/uiElements/fields/CurrencyInput'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'

export const CurrencyBase = forwardRef(({ editMode = false, workUnitId, infoWorkUnit }, ref) => {
    const translate = useTranslate()
    const notify = useNotify()
    const { control } = useFormContext()
    const [editingIndex, setEditingIndex] = useState(null);

    const [isLoadingCurrencyBase, setIsLoadingCurrencyBases] = useState(false);
    const [currencyBaseRelations, setCurrencyBaseRelations] = useState([]);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [highlightEditingIndex, setHighlightEditingIndex] = useState(null);
    const [openModalDeleteConfirmation, setOpenModalDeleteConfirmation] = useState(false);
    const [currentActionIndex, setCurrentActionIndex] = useState(null);
    const [inputValueErrors, setInputValueErrors] = useState({});
    const [currencyFromErrors, setCurrencyFromErrors] = useState({});
    const [currencyToErrors, setCurrencyToErrors] = useState({});
    const [basesByWorkUnit, setBasesByWorkUnit] = useState([]);
    const [currenciesRelationsByWorkUnit, setCurrenciesRelationsByWorkUnit] = useState([]);
    const [basesErrors, setBasesErrors] = useState({});

    useImperativeHandle(ref, () => ({
        getCurrencyBaseRelationsState: () => currencyBaseRelations
    }));

    // Begin Get Currency Bases
    const getCurrencyBases = async () => {
        setIsLoadingCurrencyBases(true);
        try {
            const defaultBase = infoWorkUnit?.billing_default_base || null;
            const alternateBaseOptions = infoWorkUnit?.billing_alternate_base?.options || [];

            let currencyBases = Array.from(
                new Set([defaultBase, ...alternateBaseOptions].filter(Boolean))
            ).map((name, index) => ({ id: index, name }));

            // Si `currencyBases` está vacío o es null, hacer la llamada adicional
            if (!currencyBases || currencyBases.length === 0) {
                const allBases = await reservationServices.getAllAlternativeBases();
                currencyBases = allBases.map((name, index) => ({ id: index, name }));
            }

            setBasesByWorkUnit(currencyBases);

            const filteredRelations = infoWorkUnit.currency_relation.filter(
                (relation) => relation.is_reverse === false
            );

            // Filtra las relaciones heredadas (si existen)
            const filteredRelationsInheritance =
                infoWorkUnit.currency_inheritance?.length > 0
                    ? infoWorkUnit.currency_inheritance.filter(
                        (relation) => relation.is_reverse === false
                    )
                    : [];
            // Filtrar las relaciones heredadas únicas
            const uniqueInheritanceRelations = filteredRelationsInheritance.filter(
                (inheritanceRelation) =>
                    !filteredRelations.some(
                        (relation) =>
                            relation.currency_relation.short_name ===
                            inheritanceRelation.currency_relation.short_name
                    )
            );

            // Combinar ambas listas
            const combinedRelations = [...filteredRelations, ...uniqueInheritanceRelations];

            setCurrenciesRelationsByWorkUnit(combinedRelations);
        } catch (error) {
            console.log(error)
            setBasesByWorkUnit([]);
            setCurrenciesRelationsByWorkUnit([]);
        } finally {
            setIsLoadingCurrencyBases(false);
        }
    };

    useEffect(() => {
        getCurrencyBases();
        getCurrencyBaseRelations(infoWorkUnit);
    }, []);

    const getCurrencyBaseRelations = async (infoWorkUnit) => {
        setLoadingInfo(true);
        try {
            let query = `work_unit_hash=${infoWorkUnit.hash_work_unit}`;
            const response = await billingFormServices.getCurrencyBaseRelations(query);

            const dataWithEditingFlag = response.data.map((item) => {
                let normalizedValue;

                // Convertir valor a string con formato de 2 decimales y con coma como separador decimal
                if (typeof item.value === 'number') {
                    normalizedValue = item.value.toFixed(4).replace(/\.?0+$/, '').replace('.', ',');
                } else if (typeof item.value === 'string') {
                    normalizedValue = item.value.includes('.')
                        ? item.value.replace('.', ',')
                        : item.value; // Aseguramos que use coma como separador
                } else {
                    normalizedValue = '0,00'; // Valor por defecto en caso de valor inesperado
                }

                return {
                    ...item,
                    value: normalizedValue, // El valor ahora está en "xxx,xx"
                    isEditing: false,
                    work_unit_hash: infoWorkUnit.hash_work_unit
                };
            });
            setCurrencyBaseRelations(dataWithEditingFlag);
        } catch (error) {
            console.error('Error fetching getCurrencyBaseRelations: ', error);
            setCurrencyBaseRelations([]);
        } finally {
            setLoadingInfo(false);
        }
    };

    const LoadingComponent = () => {
        return (
            <Grid container spacing={3} style={{ marginBottom: 50 }}>
                <Grid item xs={12} md={4} lg={4} xl={4}>
                    <Skeleton sx={{ bgcolor: 'grey.100' }} variant="rectangular" width={'100%'} height={60} />
                </Grid>
                <Grid item xs={12} md={4} lg={4} xl={4}>
                    <Skeleton sx={{ bgcolor: 'grey.100' }} variant="rectangular" width={'100%'} height={60} />
                </Grid>
                <Grid item xs={12} md={4} lg={4} xl={4}>
                    <Skeleton sx={{ bgcolor: 'grey.100' }} variant="rectangular" width={'100%'} height={60} />
                </Grid>
                <Grid item xs={12} md={12} lg={12} xl={12}>
                    <Skeleton sx={{ bgcolor: 'grey.100' }} variant="rectangular" width={'100%'} height={60} />
                </Grid>
            </Grid>
        )
    }

    const handleDelete = async (index) => {
        const currencyRelation = currencyBaseRelations[index];
        if (currencyRelation.id === null) {
            setCurrencyBaseRelations((prevState) => prevState.filter((_, i) => i !== index));
        } else {
            const data = { is_active: false };
            try {
                await billingFormServices.deleteCurrencyBaseRelation(currencyRelation.id, data);
                await getCurrencyBaseRelations(infoWorkUnit);
                notify(translate('ra.notification.relationshipDeletedSuccess'), { type: 'success' });
            } catch (error) {
                notify(translate('ra.notification.errorDeletingRelationship'), { type: 'error' });
                console.error('Error al eliminar la relación: ', error);
            }
        }
    };

    const handleAddComponent = () => {
        setCurrencyBaseRelations((prevState) => [
            ...prevState,
            {
                id: null, // Indica que es un nuevo componente
                value: '',
                currency_from_relation: { id: '', short_name: '' },
                currency_to_relation: { id: '', short_name: '' },
                work_unit_hash: infoWorkUnit.hash_work_unit,
                bases: [],
                isEditing: true,
                is_inherited: false,
            },
        ]);
        setEditingIndex(currencyBaseRelations.length);
    };

    const handleInputValueChange = (index, value) => {
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index]['value'] = value;
            return newState;
        });
    };

    const handleBasesChange = (index, value) => {
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].bases = value;
            return newState;
        });

        // Validar bases
        if (!value || value.length === 0) {
            setBasesErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Selecciona al menos una base',
            }));
        } else {
            setBasesErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handleEditClick = (index) => {
        handleEdit(index);
    };

    const handleCurrencyFromChange = (index, value) => {
        const selectedCurrency = currenciesRelationsByWorkUnit.find(
            (currency) => currency.currency_relation.short_name === value
        );
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].currency_from_relation = selectedCurrency
                ? { id: selectedCurrency.currency_relation.id, short_name: selectedCurrency.currency_relation.short_name }
                : { id: '', short_name: '' };
            // Si la moneda en 'to' es la misma, resetearla
            if (newState[index].currency_to_relation.id === value) {
                newState[index].currency_to_relation = { id: '', short_name: '' };
            }
            return newState;
        });

        // Validar
        if (!value) {
            setCurrencyFromErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Selecciona una moneda de origen',
            }));
        } else {
            setCurrencyFromErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handleCurrencyToChange = (index, value) => {
        const selectedCurrency = currenciesRelationsByWorkUnit.find(
            (currency) => currency.currency_to_relation.short_name === value
        );
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].currency_to_relation = selectedCurrency
                ? { id: selectedCurrency.currency_to_relation.id, short_name: selectedCurrency.currency_to_relation.short_name }
                : { id: '', short_name: '' };
            return newState;
        });

        // Validar
        if (!value) {
            setCurrencyToErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Selecciona una moneda de destino',
            }));
        } else {
            setCurrencyToErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].isEditing = true;
            newState[index].is_inherited = false;
            return newState;
        });
    };

    const handleCancelEdit = async (index) => {
        if (currencyBaseRelations[index].id === null) {
            setCurrencyBaseRelations((prevState) => prevState.filter((_, i) => i !== index));
        } else {
            await getCurrencyBaseRelations(infoWorkUnit);
        }
        setEditingIndex(null);
        setHighlightEditingIndex(null); // Reiniciar highlightEditingIndex

        // Clear error messages for this index
        setCurrencyFromErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });

        setCurrencyToErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });

        setInputValueErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });

        setBasesErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });
    };


    const handleOpenDeleteConfirmation = (index) => {
        setCurrentActionIndex(index);
        setOpenModalDeleteConfirmation(true);
    };

    const handleConfirmDelete = () => {
        handleDelete(currentActionIndex);
        setOpenModalDeleteConfirmation(false);
        setCurrentActionIndex(null);
    };

    const handleCancelSave = async () => {
        //setOpenModalSaveConfirmation(false);
        await getCurrencyBaseRelations(workUnitId);
        setEditingIndex(null);
        setHighlightEditingIndex(null);
    };

    const handleCheckboxChange = (index) => (event) => {
        setCurrencyBaseRelations((prevRelations) =>
            prevRelations.map((relation, i) =>
                i === index ? { ...relation, is_inherited: event.target.checked } : relation
            )
        );
    };

    const DeleteConfirmationDialog = () => {
        return (
            <Dialog maxWidth="lg" className='generalDialogContainer' open={openModalDeleteConfirmation} onClose={() => setOpenModalDeleteConfirmation(false)}>
                <DialogTitle className='dialogTitle warning'> {translate('ra.title.requestConfirmation')} </DialogTitle>
                <DialogContent dividers className='dialogContent smallNotification'>
                    <Grid container spacing={3} >
                        <Grid item xs={12} md={12} lg={12} xl={12} >
                            <div className='subtitleMessage'> <Icon className='icon'>error</Icon> {translate('ra.message.beforeDeleting')} </div>
                            <Typography variant="p" component="p" className="borderMessage">
                                {translate('ra.message.deleteCurrencyBaseConfirmation')}
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModalDeleteConfirmation(false)} color="inherit">
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="warning">
                        {translate('ra.action.to_confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Grid item xs={12} md={12} lg={12} xl={12}>
            {loadingInfo ?
                <Fragment>
                    {currencyBaseRelations?.map((index) => (
                        <LoadingComponent />
                    ))}
                </Fragment>
                :
                <Fragment>
                    <DeleteConfirmationDialog />
                    {currencyBaseRelations?.map((currencyRelation, index) => (
                        <Grid container spacing={3} key={index} id={`containerCurrencyBaseRelation-${index}`}
                            className={`containerCurrencyBaseRelation ${index === highlightEditingIndex ? 'editing' : ''}`}
                        >
                            {/* Start Bloq Editing Buttons */}
                            <Grid item xs={12} container justifyContent="flex-end" alignItems="right">

                                {!currencyRelation.isEditing && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<Icon >edit</Icon>}
                                        onClick={() => handleEditClick(index)}
                                        style={{ color: editingIndex !== null && editingIndex !== index ? 'grey' : 'primary' }}
                                    >
                                        {translate('ra.action.edit')}
                                    </Button>
                                )}

                                {currencyRelation.isEditing ? (
                                    <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<Icon>cancel</Icon>}
                                        onClick={() => handleCancelEdit(index)}
                                        color="error"
                                    >
                                        {translate('ra.action.cancel')}
                                    </Button>
                                ) : (
                                    !currencyRelation.is_inherited &&
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        startIcon={<Icon>delete</Icon>}
                                        onClick={() => handleOpenDeleteConfirmation(index)}
                                    >
                                        {translate('ra.action.delete')}
                                    </Button>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4} lg={4} xl={4}>
                                <FormControl fullWidth error={!!currencyFromErrors[index]}>
                                    <InputLabel id={`selectCurrencyFrom-${index}`}>{translate('ra.field.currency_from')} *</InputLabel>
                                    <Select
                                        labelId={`selectCurrencyFrom-${index}`}
                                        id={`selectCurrencyFrom-${index}`}
                                        value={currencyRelation.currency_from_relation.short_name || ''}
                                        label={translate('ra.field.currency_from')}
                                        onChange={(e) => handleCurrencyFromChange(index, e.target.value)}
                                        disabled={!currencyRelation.isEditing}
                                    >
                                        <MenuItem value="" disabled>
                                            <em>{translate('ra.action.select_with_dots')}</em>
                                        </MenuItem>
                                        {Array.from(new Set(currenciesRelationsByWorkUnit.map((currency) => currency.currency_relation.id)))
                                            .map((id) => {
                                                const currency = currenciesRelationsByWorkUnit.find((item) => item.currency_relation.id === id);
                                                return (
                                                    <MenuItem key={currency.currency_relation.id} value={currency.currency_relation.short_name}>
                                                        {currency.currency_relation.long_name}
                                                    </MenuItem>
                                                );
                                            })}
                                    </Select>
                                    {currencyFromErrors[index] && (
                                        <FormHelperText>{currencyFromErrors[index]}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4} lg={4} xl={4}>
                                <FormControl fullWidth error={!!currencyToErrors[index]}>
                                    <InputLabel id={`selectCurrencyTo-${index}`}>{translate('ra.field.currency_to')} *</InputLabel>
                                    <Select
                                        labelId={`selectCurrencyTo-${index}`}
                                        id={`selectCurrencyTo-${index}`}
                                        value={currencyRelation.currency_to_relation.short_name || ''}
                                        label={translate('ra.field.currency_to')}
                                        onChange={(e) => handleCurrencyToChange(index, e.target.value)}
                                        disabled={!currencyRelation.isEditing}
                                    >
                                        <MenuItem value="" disabled>
                                            <em> {translate('ra.action.select_with_dots')} </em>
                                        </MenuItem>
                                        {Array.from(new Set(currenciesRelationsByWorkUnit.map((currency) => currency.currency_to_relation.id)))
                                            .filter((id) => {
                                                const currency = currenciesRelationsByWorkUnit.find((item) => item.currency_to_relation.id === id);
                                                return currency && currency.currency_to_relation.short_name !== currencyRelation.currency_from_relation.short_name;
                                            })
                                            .map((id) => {
                                                const currency = currenciesRelationsByWorkUnit.find((item) => item.currency_to_relation.id === id);
                                                return (
                                                    <MenuItem key={currency.currency_to_relation.id} value={currency.currency_to_relation.short_name}>
                                                        {currency.currency_to_relation.long_name}
                                                    </MenuItem>
                                                );
                                            })}
                                    </Select>
                                    {currencyToErrors[index] && (
                                        <FormHelperText>{currencyToErrors[index]}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4} lg={4} xl={4}>
                                <FormControl fullWidth>
                                    <CurrencyInput
                                        fullWidth
                                        id={`inputValue-${index}`}
                                        label={`${translate('ra.field.value')} *`}
                                        placeholder="0,00"
                                        type="text"
                                        onChange={(e) => handleInputValueChange(index, e.target.value)}
                                        value={currencyRelation.value}
                                        disabled={!currencyRelation.isEditing}
                                        error={!!inputValueErrors[index]}
                                        helperText={inputValueErrors[index]}
                                        size={'medium'}
                                        maskOptions={{
                                            decimalLimit: 4, // Aquí defines los 4 decimales
                                        }}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={12} lg={12} xl={12}>
                                <Autocomplete
                                    multiple
                                    id={`autocompleteBases-${index}`}
                                    options={basesByWorkUnit}
                                    getOptionLabel={(option) => option.name}
                                    value={currencyRelation.bases}
                                    isOptionEqualToValue={(option, value) => option.name === value.name}
                                    filterSelectedOptions
                                    onChange={(event, value) => handleBasesChange(index, value)}
                                    disabled={!currencyRelation.isEditing}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Bases *"
                                            placeholder={translate('ra.action.select_with_dots')}
                                            error={!!basesErrors[index]}
                                            helperText={basesErrors[index]}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={12} lg={12} xl={12}>
                                {/* <FormControlLabel control={
                  <Checkbox
                    checked={!!currencyRelation?.is_inherited}
                    onChange={handleCheckboxChange(index)}
                    disabled={true}
                  />
                } label="Es heredado?" /> */}
                                {translate('ra.field.isInherited')}: {!!currencyRelation?.is_inherited === true ? ' Si' : ' No'}
                            </Grid>
                        </Grid>
                    ))}

                    <Button variant="outlined" color="primary" startIcon={<Add />} onClick={handleAddComponent}>
                        {translate('ra.action.addComponent')}
                    </Button>
                </Fragment>
            }
        </Grid>
    )
})